import { createRoute, Link } from "@tanstack/react-router";
import { withPermission } from "../lib/with-permission";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { rootRoute } from "./__root";
import { PageHeader } from "../components/ui/PageHeader/PageHeader";
import { Card } from "../components/ui/Card/Card";
import { Button } from "../components/ui/Button/Button";
import { Badge } from "../components/ui/Badge/Badge";
import { ConfirmDialog } from "../components/ui/ConfirmDialog/ConfirmDialog";
import { LoadingSpinner } from "../components/ui/LoadingSpinner/LoadingSpinner";
import { ErrorFallback } from "../components/ui/ErrorFallback/ErrorFallback";
import {
	useOfferDetail,
	useSendOffer,
	useDeleteOffer,
	useConvertToOrder,
} from "../hooks/use-offers";
import { BackButton } from "../components/ui/BackButton/BackButton";
import { formatDate, formatAmount } from "../lib/date";
import styles from "./OfferDetail.module.css";

export const route = createRoute({
	getParentRoute: () => rootRoute,
	path: "/offers/$id",
	component: withPermission(OfferDetailPage, "offers", "read"),
});

function OfferDetailPage() {
	const { id } = route.useParams();
	const { t } = useTranslation();
	const offerQuery = useOfferDetail(id);
	const sendOffer = useSendOffer();
	const deleteOffer = useDeleteOffer();
	const convertToOrder = useConvertToOrder();
	const [sendOpen, setSendOpen] = useState(false);
	const [deleteOpen, setDeleteOpen] = useState(false);
	const [convertOpen, setConvertOpen] = useState(false);
	const [isCustomDesign, setIsCustomDesign] = useState(false);
	const [selectedAlternative, setSelectedAlternative] = useState<number | null>(null);

	if (offerQuery.isLoading) return <LoadingSpinner />;
	if (offerQuery.isError || !offerQuery.data) return <ErrorFallback onRetry={offerQuery.refetch} />;

	const offer = offerQuery.data;

	const alternativeGroups = [
		...new Map(
			(offer.lines ?? []).map((l) => [
				l.alternativeGroup ?? 0,
				{
					group: l.alternativeGroup ?? 0,
					currency: l.alternativeCurrency ?? offer.currency,
				},
			]),
		).values(),
	].sort((a, b) => a.group - b.group);
	const groupTotal = (group: number) =>
		(offer.lines ?? [])
			.filter((l) => (l.alternativeGroup ?? 0) === group)
			.reduce(
				(sum, l) =>
					sum +
					Number(l.quantity) * Number(l.unitPrice) * (1 - Number(l.discountPercent ?? 0) / 100),
				0,
			);
	const groupTitle = (group: number) => {
		const first = (offer.lines ?? []).find((l) => (l.alternativeGroup ?? 0) === group);
		return first?.displayDescription || first?.itemDescription || `#${group + 1}`;
	};
	const isMultiAlternative = alternativeGroups.length > 1;

	const hasAnyTerms =
		offer.termsCommissioning ||
		offer.termsAssembly ||
		offer.termsVat ||
		offer.termsQuality ||
		offer.termsValidity ||
		offer.termsWarranty ||
		offer.termsPayment ||
		offer.termsDelivery;

	const hasAnySapFields =
		offer.startupDescription ||
		offer.installationDescription ||
		offer.tmsDescription ||
		offer.atsDescription ||
		offer.factorySpecialNotes ||
		offer.generalNotes;

	return (
		<div>
			<BackButton fallbackTo="/offers" />
			<PageHeader
				title={offer.documentNumber || `${t("offer")} #${offer.id.slice(0, 8)}`}
				action={
					<div className={styles.actions}>
						{offer.isLatestVersion && offer.status !== "converted_to_order" && (
							<Link to="/offers/$id/edit" params={{ id: offer.id }}>
								<Button>{t("edit")}</Button>
							</Link>
						)}
						{offer.status === "draft" && (
							<Button onClick={() => setSendOpen(true)}>{t("sendConvertToOrder")}</Button>
						)}
						{(offer.status === "sent" || offer.status === "accepted") && (
							<Button
								onClick={() => {
									setSelectedAlternative(
										isMultiAlternative ? null : (alternativeGroups[0]?.group ?? 0),
									);
									setConvertOpen(true);
								}}
							>
								{t("convertToOrder")}
							</Button>
						)}
						<Link
							to="/contracts/new"
							search={{
								companyId: offer.companyId,
								orderId: "",
							}}
						>
							<Button variant="secondary" size="sm">
								{t("createContract")}
							</Button>
						</Link>
						<Button variant="danger" onClick={() => setDeleteOpen(true)}>
							{t("delete")}
						</Button>
					</div>
				}
			/>

			<div className={styles.grid}>
				<Card header={t("generalInfo")}>
					<div className={styles.infoGrid}>
						<Info
							label={t("company")}
							value={
								offer.companyId ? (
									<Link to={`/companies/${offer.companyId}` as never} className={styles.link}>
										{offer.companyName ?? "-"}
									</Link>
								) : (
									"-"
								)
							}
						/>
						<Info
							label={t("validUntil")}
							value={offer.validUntil ? formatDate(offer.validUntil) : "-"}
						/>
						<Info label={t("currency")} value={offer.currency} />
						<Info label={t("paymentTerms")} value={offer.paymentTerms} />
						<Info label={t("deliveryTerms")} value={offer.deliveryTerms} />
						<Info
							label={t("offerDate")}
							value={offer.offerDate ? formatDate(offer.offerDate) : "-"}
						/>
						<Info
							label={t("commitmentDuration")}
							value={
								offer.commitmentValue != null && offer.commitmentUnit
									? `${offer.commitmentValue} ${t(
											offer.commitmentUnit === "day"
												? "unitDay"
												: offer.commitmentUnit === "week"
													? "unitWeek"
													: "unitMonth",
										)}`
									: offer.commitmentDate
										? formatDate(offer.commitmentDate)
										: "-"
							}
						/>
						<Info label={t("isTaxIncluded")} value={offer.isTaxIncluded ? t("yes") : t("no")} />
						<Info label={t("factoryNotes")} value={offer.notes ?? "-"} />
						<Info label={t("pdfNotes")} value={offer.pdfNotes ?? "-"} />
						<Info label={t("grossTotal")} value={formatAmount(offer.grossTotal)} />
						<div>
							<span className={styles.infoLabel}>{t("status")}</span>
							<Badge
								variant={
									offer.status === "sent"
										? "primary"
										: offer.status === "accepted"
											? "success"
											: offer.status === "rejected"
												? "danger"
												: "default"
								}
							>
								{t(offer.status)}
							</Badge>
						</div>
					</div>
				</Card>

				{hasAnyTerms && (
					<Card header={t("terms")}>
						<div className={styles.infoGrid}>
							{offer.termsCommissioning && (
								<Info label={t("termsCommissioning")} value={offer.termsCommissioning} />
							)}
							{offer.termsAssembly && (
								<Info label={t("termsAssembly")} value={offer.termsAssembly} />
							)}
							{offer.termsVat && <Info label={t("termsVat")} value={offer.termsVat} />}
							{offer.termsQuality && <Info label={t("termsQuality")} value={offer.termsQuality} />}
							{offer.termsValidity && (
								<Info label={t("termsValidity")} value={offer.termsValidity} />
							)}
							{offer.termsWarranty && (
								<Info label={t("termsWarranty")} value={offer.termsWarranty} />
							)}
							{offer.termsPayment && <Info label={t("termsPayment")} value={offer.termsPayment} />}
							{offer.termsDelivery && (
								<Info label={t("termsDelivery")} value={offer.termsDelivery} />
							)}
						</div>
					</Card>
				)}

				{hasAnySapFields && (
					<Card header={t("sapFields")}>
						<div className={styles.infoGrid}>
							{offer.startupDescription && (
								<Info label={t("startupDescription")} value={offer.startupDescription} />
							)}
							{offer.installationDescription && (
								<Info label={t("installationDescription")} value={offer.installationDescription} />
							)}
							{offer.tmsDescription && (
								<Info label={t("tmsDescription")} value={offer.tmsDescription} />
							)}
							{offer.atsDescription && (
								<Info label={t("atsDescription")} value={offer.atsDescription} />
							)}
							{offer.factorySpecialNotes && (
								<Info label={t("factorySpecialNotes")} value={offer.factorySpecialNotes} />
							)}
							{offer.generalNotes && <Info label={t("generalNotes")} value={offer.generalNotes} />}
						</div>
					</Card>
				)}

				<Card header={t("lineItems")}>
					{(offer.lines ?? []).length === 0 && <p className={styles.empty}>{t("noData")}</p>}
					<table className={styles.lineTable}>
						<thead>
							<tr>
								<th>{t("itemCode")}</th>
								<th>{t("itemDescription")}</th>
								<th>{t("displayDescription")}</th>
								<th>{t("quantity")}</th>
								<th>{t("listPrice")}</th>
								<th>{t("givenPrice")}</th>
								<th>{t("discountPercent")}</th>
								<th>{t("lineTotal")}</th>
								<th>{t("lineDetail")}</th>
								<th>{t("showInPdf")}</th>
								<th>{t("showPriceInPdf")}</th>
								<th>{t("showDiscountInPdf")}</th>
								<th>Datasheet</th>
								<th>{t("sendDatasheetInEmail")}</th>
							</tr>
						</thead>
						<tbody>
							{(offer.lines ?? []).map((line) => (
								<tr key={line.id}>
									<td>{line.itemCode || "-"}</td>
									<td>{line.itemDescription || "-"}</td>
									<td>{line.displayDescription || line.itemDescription || "-"}</td>
									<td>{line.quantity}</td>
									<td>{formatAmount(line.unitPrice)}</td>
									<td>
										{(() => {
											const list = Number(line.unitPrice) || 0;
											const disc = Number(line.discountPercent) || 0;
											const given =
												line.givenUnitPrice != null && line.givenUnitPrice !== ""
													? Number(line.givenUnitPrice)
													: Math.round(list * (1 - disc / 100) * 100) / 100;
											return formatAmount(given);
										})()}
									</td>
									<td>
										{(() => {
											const disc = Number(line.discountPercent) || 0;
											const variant = disc > 0 ? "danger" : disc < 0 ? "success" : "default";
											const label =
												disc > 0 ? t("belowList") : disc < 0 ? t("aboveList") : t("atList");
											return (
												<Badge variant={variant}>
													%{formatAmount(line.discountPercent) || "0"} · {label}
												</Badge>
											);
										})()}
									</td>
									<td>{formatAmount(line.lineTotal)}</td>
									<td>{line.lineDetail || "-"}</td>
									<td>{line.showInPdf ? t("yes") : t("no")}</td>
									<td>{line.showPriceInPdf ? t("yes") : t("no")}</td>
									<td>{line.showDiscountInPdf ? t("yes") : t("no")}</td>
									<td>
										{line.itemDescription && (
											<Button
												size="sm"
												variant="ghost"
												onClick={async () => {
													try {
														const res = await fetch(
															`/api/nextcloud/datasheet?desc=${encodeURIComponent(line.itemDescription!)}`,
															{ credentials: "include" },
														);
														const data = (await res.json()) as { path?: string; error?: string };
														if (!res.ok) {
															alert(data.error ?? t("datasheetNotFound"));
															return;
														}
														window.open(
															`/api/nextcloud/file?path=${encodeURIComponent(data.path!)}`,
															"_blank",
														);
													} catch {
														alert(t("datasheetNotFound"));
													}
												}}
											>
												{t("view")}
											</Button>
										)}
									</td>
									<td>{line.sendDatasheetInEmail ? t("yes") : t("no")}</td>
								</tr>
							))}
						</tbody>
					</table>
				</Card>

				<Card>
					<div className={styles.totals}>
						<div className={styles.totalRow}>
							<span>{t("subtotal")}</span>
							<span>
								{formatAmount(offer.subtotal)} {offer.currency}
							</span>
						</div>
						<div className={styles.totalRow}>
							<span>{t("vatTotal")}</span>
							<span>
								{formatAmount(offer.vatTotal)} {offer.currency}
							</span>
						</div>
						<div className={[styles.totalRow, styles.grandTotal].join(" ")}>
							<span>{t("total")}</span>
							<span>
								{formatAmount(offer.total)} {offer.currency}
							</span>
						</div>
					</div>
				</Card>
			</div>

			<ConfirmDialog
				isOpen={sendOpen}
				onClose={() => setSendOpen(false)}
				onConfirm={() =>
					sendOffer.mutate(id, {
						onSuccess: () => {
							setSendOpen(false);
							alert(t("sendSuccess"));
						},
					})
				}
				message={t("confirmSend")}
				isLoading={sendOffer.isPending}
			/>

			<ConfirmDialog
				isOpen={convertOpen}
				onClose={() => setConvertOpen(false)}
				confirmDisabled={isMultiAlternative && selectedAlternative === null}
				onConfirm={() =>
					convertToOrder.mutate(
						{
							id,
							isCustomDesign,
							alternativeGroup: isMultiAlternative ? (selectedAlternative ?? undefined) : undefined,
						},
						{
							onSuccess: (data) => {
								setConvertOpen(false);
								alert(t("convertToOrder") + " " + t("createSuccess").toLowerCase());
								window.location.href = `/orders/${data.id}`;
							},
						},
					)
				}
				message={isMultiAlternative ? t("selectAlternativeToConvert") : t("confirmConvertToOrder")}
				isLoading={convertToOrder.isPending}
			>
				{isMultiAlternative && (
					<div className={styles.alternativeChoices}>
						{alternativeGroups.map(({ group, currency }, idx) => (
							<label key={group} className={styles.alternativeChoice}>
								<input
									type="radio"
									name="convert-alternative"
									checked={selectedAlternative === group}
									onChange={() => setSelectedAlternative(group)}
								/>
								<span>
									<strong>
										{t("alternative")} {idx + 1}
									</strong>{" "}
									— {groupTitle(group)} · {formatAmount(groupTotal(group))} {currency}
								</span>
							</label>
						))}
					</div>
				)}
				<label className={styles.checkboxLabel}>
					<input
						type="checkbox"
						checked={isCustomDesign}
						onChange={(e) => setIsCustomDesign(e.target.checked)}
					/>
					<span>{t("isCustomDesign")}</span>
				</label>
			</ConfirmDialog>

			<ConfirmDialog
				isOpen={deleteOpen}
				onClose={() => setDeleteOpen(false)}
				onConfirm={() =>
					deleteOffer.mutate(id, {
						onSuccess: () => {
							setDeleteOpen(false);
							alert(t("deleteSuccess"));
						},
					})
				}
				message={t("confirmDelete")}
				isLoading={deleteOffer.isPending}
			/>
		</div>
	);
}

function Info({ label, value }: { label: string; value: React.ReactNode }) {
	return (
		<div>
			<span className={styles.infoLabel}>{label}</span>
			<p className={styles.infoValue}>{value || "-"}</p>
		</div>
	);
}
